import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { getPOSConfigSnapshot } from '@/api/config'
import { effectiveUnitPrice } from '@/lib/pricing'
import { blurActiveTextInput } from '@/hooks/useBarcodeScanner'
import type { CartItem, Customer, Product, ViewMode, ActiveTab, UIProductFilters } from '@/types'
import type { V2SourcePayment } from '@/types/v2'
import { useCartStore } from './cartStore'
import { useUIStore } from './uiStore'

// Delivery-fee sentinel (DeliverySpecialProduct, id -123 on the BE). The
// delivery charge is part of a delivery order's contract — it can't be
// removed or re-quantified from the POS cart. CartItemRow disables its
// controls, but `disabled` is cosmetic; these store mutators are the actual
// enforcement (keyboard / scan / command-palette removals route here too,
// and a click on a disabled button can still reach a parent handler).
const DELIVERY_PRODUCT_ID = -123

function isDeliveryLine(cart: CartItem[], itemId: string): boolean {
  const item = cart.find((i) => i.id === itemId)
  return item?.product_id === DELIVERY_PRODUCT_ID
}

interface RecalledItem {
  product_id: number
  qty: number | string
  price: number | string
  description?: string
  discount_amount?: number | string
  lot_number?: string | null
  // subtotal: pre-tax line amount (qty × price), what the line contributes
  // to receipt subtotal. Preferred for cart-line display.
  subtotal?: number | string
  // total: post-tax + post-deposit line total. Kept for fallbacks but NOT
  // used as cart-line display anymore — see loadReceiptIntoCart.
  total?: number | string
  deposit_amount?: number | string
  is_cannabis?: boolean
  basket_id?: number | null
  basket_qty?: number | string | null
  // Server-persisted scan-to-confirm-lot state. When true the line was
  // already scanned/confirmed on a prior hold/commit, so the recall must
  // load it confirmed rather than re-arming the cashier to re-scan.
  scan_confirmed?: boolean
}

// Subset of receipt-item fields the cart back-fill cares about. Matches
// V2-API.md ReceiptItem; loose `unknown` fallbacks because the full receipt
// type lives in src/types/v2.ts and we don't need every field here.
export interface ServerReceiptItem {
  id?: number
  product_id: number
  qty: number | string
  price: number | string
  total?: number | string
  subtotal?: number | string
  discount_amount?: number | string | null
  lot_number?: string | null
  // Basket linkage; null on non-basket lines. When set, the cart back-fill
  // groups all siblings under the basket's product_id, not the child's.
  basket_id?: number | null
  basket_qty?: string | number | null
}

// Default UI filters
export const DEFAULT_UI_FILTERS: UIProductFilters = {
  weights: [],
  productTypes: [],
  thcRange: [0, 100],
  cbdRange: [0, 100],
  brands: [],
  priceRanges: [],
  categoryGroups: [],
  units: [],
}

// Server-confirmed totals from the backend receipt response.
// When set, these are always used over any client-side calculation.
export interface ServerTotals {
  subtotal: number       // net pre-tax
  taxes: number          // total tax
  deposit: number        // deposit/recycling fees
  total: number          // gross amount customer pays
  amountToPay: number    // remaining balance (after partial payments)
}

// True when the active sale receipt has at least one payment applied but
// isn't fully paid yet. Source of truth for the cart-edit lock: editing
// items on a partially-paid receipt creates a FE/BE divergence where the
// BE has charged against the old cart shape and the local cart has
// drifted. Tolerance covers BE Decimal rounding -- amountToPay can be
// 0.004 on a "fully paid" receipt; never compare floats raw.
// Refund mode is excluded; refund flows mutate against a different
// ledger and have their own per-line guards.
export function isReceiptPartiallyPaid(
  serverTotals: ServerTotals | null,
  isRefundMode: boolean,
): boolean {
  if (isRefundMode) return false
  if (!serverTotals) return false
  return (
    serverTotals.amountToPay > 0.005
    && serverTotals.amountToPay < serverTotals.total - 0.005
  )
}

// Staged SpringBig reward/offer picked from the loyalty drawer. `amount`
// is the dollar value shown in the cart chip; `label` is the display
// string. `discountType` mirrors SpringBig's `discount_type` field — when
// set to '%', the BE recomputes the discount as a percentage of the
// receipt total at pay-time, and the wire `amount` is the percent itself
// (carried in `rawAmount`) rather than dollars. Nulled out by clearCart /
// clearReceipt and after successful pay.
export interface PendingLoyalty {
  id: number
  amount: number
  label: string
  discountType?: string
  rawAmount?: number
}

interface POSState {
  // Current receipt/cart
  currentReceiptId: number | null
  // Set when the loaded receipt is an ecom order pulled into the register
  // (Finish Sale flips it Hold->New). Carries the status it had BEFORE the
  // load so "Close" can revert it (the clerk is just viewing the order, the
  // customer isn't present -- closing must NOT cancel it). null for normal
  // POS carts and recalled held receipts. `isDelivery` is for messaging only.
  // `paidOnline`: the order was PAID via the online checkout (PaidOnline=32,
  // funds captured at the gateway). The cart is a read-only viewing surface:
  // no item edits/adds (the charge must match the cart exactly), Checkout
  // becomes "Complete" (complete-pickup), and Close is local-only (the
  // receipt's status was never flipped to load it).
  loadedEcomMeta: { isDelivery: boolean; priorStatus: number; paidOnline?: boolean } | null
  lastCompletedReceiptId: number | null
  cart: CartItem[]
  // Server-confirmed totals -- legacy. Live cart totals now come from
  // cartStore.preview (see useV2PreviewBridge). Kept on the store only
  // because a couple of legacy paths (refund mode, recall flow) still
  // populate it for back-compat. Will be dropped when those move to v2.
  serverTotals: ServerTotals | null

  // Selected customer
  selectedCustomer: Customer | null

  // UI state
  viewMode: ViewMode
  activeTab: ActiveTab

  // Search/filter state
  searchQuery: string
  selectedCategoryId: number | null
  uiFilters: UIProductFilters

  // Refund state
  isRefundMode: boolean
  refundSourceReceipt: any | null
  // Original payments from the source receipt as returned by
  // /copy-with-refund. Drives the refund-method gating in CheckoutModal:
  // only payment types that appear here can be selected, and (for splits)
  // each method is capped at its corresponding source amount.
  refundSourcePayments: V2SourcePayment[] | null
  // True while a refund-cart item mutation (PUT qty / DELETE line) is
  // in flight on the persisted refund receipt. Set by
  // useRefundCartMutations; consumed by CartSummary (to dim totals
  // during the round trip) and CheckoutModal (to disable Process
  // Refund so the cashier can't commit against unconfirmed state).
  isRefundMutating: boolean

  // True once the cashier has answered "Yes" to the ID-check prompt for
  // the transaction currently in the till. Scoped to the transaction, not
  // the session: cleared by clearCart/clearReceipt so every new sale gets
  // its own prompt. Mirrors V1's `POS.STATE.age_checked` observable, which
  // was reset by `_onReceiptChange` on RECEIPT_SET / RECEIPT_START.
  // Deliberately NOT persisted (absent from `partialize`) — after a refresh
  // the safe default is to ask again.
  ageChecked: boolean

  // SpringBig redemption staged from the loyalty drawer. Read at pay-time
  // by CheckoutModal — fires a POINTS payment carrying reward_id/offer_id
  // before the cashier's chosen tender, so amount_to_pay reflects the
  // redemption. Mutually exclusive in practice (cashier picks one or the
  // other from the drawer), but we keep separate slots so the UI doesn't
  // need to disambiguate.
  pendingReward: PendingLoyalty | null
  pendingOffer: PendingLoyalty | null
}

interface POSActions {
  // Receipt management
  setReceiptId: (id: number | null) => void
  setLoadedEcomMeta: (
    meta: { isDelivery: boolean; priorStatus: number; paidOnline?: boolean } | null,
  ) => void
  setServerTotals: (totals: ServerTotals | null) => void
  setIsRefundMutating: (mutating: boolean) => void
  loadReceiptIntoCart: (
    receiptId: number,
    items: RecalledItem[],
    customer?: Customer | null,
    options?: {
      isRefund?: boolean
      refundSourceReceipt?: any
      // Optional catalog product map keyed by product_id. When the
      // recall flow can resolve the full Product (image, brand,
      // THC%, on_hand, etc.) we merge those fields onto the
      // synthetic cart-line product so recalled rows render the
      // same as freshly-added ones. Receipt-derived values
      // (price, deposit, qty, lot) always win over the catalog.
      productMap?: Map<number, Product>
      // FIFO lots resolved by the recall pipeline, keyed by product_id.
      // Ecom/hold receipts arrive with no lot on their cannabis lines, so
      // applyReceiptToCart fetches batches and picks the FIFO lot up front;
      // we stamp it here (lot resolution must happen BEFORE the cart is
      // built — calling changeBatch afterward would wipe serverTotals).
      lotMap?: Map<number, string>
      // When true (frontend.on_hold_scan_to_confirm_lot is on and this is a
      // non-refund hold/ecom recall), cannabis lines load as scan_confirmed:
      // false so the cashier must re-scan each before checkout.
      scanConfirm?: boolean
    },
  ) => void

  // Cart management
  addToCart: (
    product: Product,
    quantity?: number,
    lotNumberOrId?: number | string | null,
    options?: { forceNewLine?: boolean },
  ) => void
  updateCartItem: (itemId: string, updates: Partial<CartItem>) => void
  changeBatch: (itemId: string, newBatchNumber: string) => void
  // Mark a scan-to-confirm cart line confirmed. The gate is presence: a
  // matching scan verifies the cashier physically has the item, so confirming
  // does NOT require the barcode to carry a lot. When the scan is a GS1 with a
  // lot (AI 10) we stamp it — the physical batch overrides the FIFO default;
  // a plain UPC confirms and keeps the FIFO-assigned lot. Unlike changeBatch
  // this preserves serverTotals — confirming is metadata, not a re-price.
  confirmScanLine: (itemId: string, lotNumber?: string | null) => void
  removeFromCart: (itemId: string) => void
  updateQuantity: (itemId: string, quantity: number) => void
  changeQuantity: (itemId: string, delta: number) => void
  clearCart: () => void
  clearReceipt: () => void
  // Record the cashier's answer to the checkout ID-check prompt.
  setAgeChecked: (checked: boolean) => void
  // Refresh cart-line prices/discounts from authoritative receipt items.
  // Called by the sync hook after a successful sync so the cart line
  // reflects the server's customer-tier-adjusted price + discount, not the
  // optimistic shelf price set on add.
  applyServerItemPrices: (items: ServerReceiptItem[]) => void
  // Append a gift-card receipt line (just returned by /receipts/{id}/items)
  // to the local cart. Gift cards have no Product in the catalog — the BE
  // assigns a product_id + description on the response, which we mirror
  // into a synthetic CartItem so the cart UI shows the line.
  addGiftCardLine: (item: {
    id: number
    product_id: number
    description: string | null
    gift_card_number: string | null
  }, balance: number) => void

  // Customer management
  setCustomer: (customer: Customer | null) => void

  // UI actions
  setViewMode: (mode: ViewMode) => void
  setActiveTab: (tab: ActiveTab) => void
  setSearchQuery: (query: string) => void
  setSelectedCategory: (categoryId: number | null) => void
  setUIFilters: (filters: UIProductFilters) => void
  clearUIFilters: () => void

  // SpringBig redemption staging
  setPendingReward: (reward: PendingLoyalty | null) => void
  setPendingOffer: (offer: PendingLoyalty | null) => void
  clearPendingLoyalty: () => void

  // Refund actions
  enterRefundMode: (sourceReceipt: any, sourcePayments?: V2SourcePayment[] | null) => void
  exitRefundMode: () => void
  loadRefundItems: (items: any[], productMap?: Map<number, Product | null>) => void
  // Replace the in-store refund destination receipt after a partial-
  // refund payment lands. Callers pass the BE response payload; the
  // store keeps it so a close+reopen of CheckoutModal can re-seed
  // refundedSoFarByMethod synchronously instead of flashing the empty-
  // cap UI while a refetch is in flight.
  updateRefundReceipt: (receipt: any) => void

  // Computed helpers
  getCartTotal: () => number
  getCartItemCount: () => number
}

type POSStore = POSState & POSActions

// Helper to generate unique cart item ID
const generateCartItemId = () => `cart_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`

// Calculate local line total for client-side display (before receipt is created on backend).
//
// KEY INSIGHT from legacy POS:
// - The product API `price` field is the DISPLAY price (what customer pays per unit, tax-inclusive for cannabis)
// - The backend uses `get_tax_except_price` which returns the gross tax-inclusive price for cannabis
//   and the pre-tax price for non-cannabis
// - Deposit (pbs.deposit_price) is added by the BACKEND when the item is added to the receipt
//   We do NOT add deposit client-side — we use server totals after the item is saved
// - For non-cannabis: API price is pre-tax, backend adds 13% HST → display subtotal = price × qty
//   The checkout amount will include tax (server calculates it)
// - We keep client display simple: line_total = price × qty (no tax, no deposit added client-side)
//   Server totals replace client totals once the receipt is created
const calculateLineTotal = (item: Partial<CartItem>): number => {
  const unitPrice = item.unit_price ?? 0
  const quantity = item.quantity ?? 1
  const discountAmount = item.discount_amount ?? 0
  // Discount reduces magnitude in both directions: a sale charges less, a
  // refund returns less. For refund lines (negative qty) the gross
  // unit_price * quantity is already negative, so subtracting a positive
  // discount inflates the refund. Flip the sign so the discount always
  // pulls the line total toward zero.
  const signedDiscount = quantity < 0 ? -discountAmount : discountAmount
  return (unitPrice * quantity) - signedDiscount
}

// Per-unit discount must stay constant when qty changes via the cart row
// +/- buttons or a re-add bump. Stored `discount_amount` is a line total
// (= per-unit × qty), so scale by the qty ratio. e.g. $5 line at qty=1
// (= $5/unit) → qty=3 should be $15 line ($5/unit). Without this, $5/unit
// would silently decay to $5/3 = $1.67/unit on the bump.
const scaleLineDiscount = (
  oldDiscount: number,
  oldQty: number,
  newQty: number,
): number => {
  if (!oldDiscount || oldDiscount <= 0) return 0
  const oldAbs = Math.abs(oldQty)
  if (oldAbs === 0) return 0
  return +((oldDiscount / oldAbs) * Math.abs(newQty)).toFixed(2)
}

export const usePOSStore = create<POSStore>()(
  persist(
    (set, get) => ({
      // Initial state
      currentReceiptId: null,
      loadedEcomMeta: null,
      lastCompletedReceiptId: null,
      cart: [],
      serverTotals: null,
      selectedCustomer: null,
      viewMode: 'grid',
      activeTab: 'products',
      searchQuery: '',
      selectedCategoryId: null,
      uiFilters: DEFAULT_UI_FILTERS,
      isRefundMode: false,
      refundSourceReceipt: null,
      refundSourcePayments: null,
      isRefundMutating: false,
      ageChecked: false,
      pendingReward: null,
      pendingOffer: null,

      // SpringBig redemption staging — picking a reward also clears any
      // staged offer (and vice versa) so the cashier can't accidentally
      // stack two redemptions at pay-time.
      setPendingReward: (reward) =>
        set({ pendingReward: reward, ...(reward ? { pendingOffer: null } : {}) }),
      setPendingOffer: (offer) =>
        set({ pendingOffer: offer, ...(offer ? { pendingReward: null } : {}) }),
      clearPendingLoyalty: () => set({ pendingReward: null, pendingOffer: null }),

      // Receipt management
      setReceiptId: (id) => set({ currentReceiptId: id }),
      setLoadedEcomMeta: (meta) => set({ loadedEcomMeta: meta }),
      setServerTotals: (totals) => set({ serverTotals: totals }),
      setIsRefundMutating: (mutating) => set({ isRefundMutating: mutating }),
      setAgeChecked: (checked) => set({ ageChecked: checked }),

      loadReceiptIntoCart: (receiptId, items, customer = null, options) => {
        const isRefund = options?.isRefund === true
        const productMap = options?.productMap

        // Pre-pass: collapse basket children into one synthetic recall row
        // per basket_id. The basket SKU itself is never persisted as a
        // receipt line; we rebuild it client-side so the recalled cart
        // shows one line per basket instead of N child rows. See
        // V2-API.md § Baskets.
        const groupedItems: RecalledItem[] = []
        const basketGroups = new Map<number, {
          basket_id: number
          basket_qty: number
          subtotalSum: number
          discountSum: number
          depositSum: number
          firstChild: RecalledItem
        }>()

        for (const item of items) {
          if (!item.basket_id) {
            groupedItems.push(item)
            continue
          }
          const basketId = Number(item.basket_id)
          const basketQty = Math.abs(
            typeof item.basket_qty === 'number'
              ? item.basket_qty
              : parseFloat(String(item.basket_qty ?? '')) || 1,
          ) || 1
          // Aggregate pre-tax subtotals — cart-line display is pre-tax,
          // so the basket's collapsed line shows the sum of children's
          // subtotal contributions (matches receipt subtotal).
          const subtotal = item.subtotal != null
            ? (typeof item.subtotal === 'number' ? item.subtotal : parseFloat(String(item.subtotal)) || 0)
            : 0
          const discount = typeof item.discount_amount === 'number'
            ? item.discount_amount
            : parseFloat(String(item.discount_amount ?? '')) || 0
          const deposit = item.deposit_amount != null
            ? (typeof item.deposit_amount === 'number' ? item.deposit_amount : parseFloat(String(item.deposit_amount)) || 0)
            : 0

          const existing = basketGroups.get(basketId)
          if (existing) {
            existing.subtotalSum += Math.abs(subtotal)
            existing.discountSum += discount
            existing.depositSum += Math.abs(deposit)
          } else {
            basketGroups.set(basketId, {
              basket_id: basketId,
              basket_qty: basketQty,
              subtotalSum: Math.abs(subtotal),
              discountSum: discount,
              depositSum: Math.abs(deposit),
              firstChild: item,
            })
          }
        }

        for (const group of basketGroups.values()) {
          // Synthesize a single recall row representing the basket.
          // qty = basket_qty (cashier's original add count),
          // subtotal = sum of children's pre-tax subtotals,
          // price = subtotal ÷ qty (per-basket pre-tax price).
          groupedItems.push({
            ...group.firstChild,
            product_id: group.basket_id,
            qty: group.basket_qty,
            price: group.basket_qty > 0 ? group.subtotalSum / group.basket_qty : group.subtotalSum,
            subtotal: group.subtotalSum,
            discount_amount: group.discountSum,
            deposit_amount: group.depositSum,
            description: `Basket #${group.basket_id}`,
            lot_number: null,
            basket_id: group.basket_id,
            basket_qty: group.basket_qty,
          })
        }

        const cartItems: CartItem[] = groupedItems.map((item) => {
          // Backend always stores qty as positive on a refund receipt; we
          // mirror back to a signed cart quantity so the UI shows the refund
          // styling (red badge, negative line total).
          const rawQty = typeof item.qty === 'number' ? item.qty : parseFloat(String(item.qty)) || 1
          const absQty = Math.abs(rawQty)
          const qty = isRefund ? -absQty : rawQty
          const discountAmount = typeof item.discount_amount === 'number' ? item.discount_amount : parseFloat(String(item.discount_amount)) || 0

          // unit_price = the per-unit price on the receipt item (matches
          // shelf / product card). line_total = pre-tax line subtotal so
          // sum(cart lines) === serverTotals.subtotal. Tax shows only in
          // the dedicated row at the bottom — it's never embedded in the
          // line amount the cashier reads.
          const netPrice = typeof item.price === 'number' ? item.price : parseFloat(String(item.price)) || 0
          const subtotal = item.subtotal != null
            ? (typeof item.subtotal === 'number' ? item.subtotal : parseFloat(String(item.subtotal)) || 0)
            : null
          const unitPrice = netPrice
          const baseLineTotal = subtotal != null
            ? Math.abs(subtotal)
            : (netPrice * absQty) - discountAmount
          const lineTotal = isRefund ? -baseLineTotal : baseLineTotal

          const depositAmount = item.deposit_amount != null
            ? (typeof item.deposit_amount === 'number' ? item.deposit_amount : parseFloat(String(item.deposit_amount)) || 0)
            : 0

          // When the catalog has the full Product (recall flow looks it
          // up before calling us), spread it underneath so display-only
          // fields (image, brand, strain, THC/CBD %, on_hand, tags)
          // come through. The receipt-derived fields below override:
          // price/deposit reflect what the customer was charged, qty
          // and lot are receipt facts, and is_cannabis determines tax
          // behavior so the receipt's value wins.
          const fullProduct = productMap?.get(item.product_id)
          const syntheticProduct: Product = {
            ...(fullProduct ?? {}),
            id: item.product_id,
            description:
              item.description ?? fullProduct?.description ?? `Product ${item.product_id}`,
            price: unitPrice,
            is_cannabis: item.is_cannabis ?? fullProduct?.is_cannabis ?? false,
            deposit_price: depositAmount > 0 ? Math.abs(depositAmount) / absQty : 0,
          }
          return {
            id: `recalled_${item.product_id}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
            product_id: item.product_id,
            product: syntheticProduct,
            quantity: qty,
            unit_price: unitPrice,
            discount_amount: discountAmount,
            discount_percent: 0,
            tax_amount: 0,   // embedded in unit_price for recalled items
            line_total: lineTotal,
            lot_id: null,
            // Receipt lot wins; otherwise the FIFO lot the recall pipeline
            // resolved for this product (ecom/hold lines arrive lot-less).
            lot_number: item.lot_number ?? options?.lotMap?.get(item.product_id) ?? null,
            // Arm scan-to-confirm on cannabis lines; other lines stay N/A.
            // Basket rows are excluded: their synthetic product_id is the
            // basket_id, which has no scannable barcode of its own, so a scan
            // could never match and confirm them — arming would hard-deadlock
            // checkout. (Baskets are bundles; their lot handling is separate.)
            // A line the server already marked confirmed loads confirmed (no
            // re-prompt); genuinely unscanned lines load false and prompt.
            ...(options?.scanConfirm && syntheticProduct.is_cannabis && !item.basket_id
              ? { scan_confirmed: item.scan_confirmed === true }
              : {}),
            notes: null,
            ...(item.basket_id ? { basket_id: Number(item.basket_id) } : {}),
            ...(isRefund ? { max_refund_qty: absQty } : {}),
          }
        })
        set({
          currentReceiptId: receiptId,
          // Default to "not ecom" on every load; the ecom Finish-Sale flow
          // (useFinishEcomSale) sets it explicitly via setLoadedEcomMeta
          // right after this runs. Clearing it here stops a prior ecom load
          // from bleeding into a subsequent held/refund recall.
          loadedEcomMeta: null,
          cart: cartItems,
          selectedCustomer: customer ?? null,
          serverTotals: null, // will be set by HeldReceiptsModal after unhold
          // A recalled/unheld/refund receipt replaces the transaction
          // wholesale without passing through an empty cart, so the ID
          // check must be answered again for it.
          ageChecked: false,
          ...(isRefund
            ? {
                isRefundMode: true,
                refundSourceReceipt: options?.refundSourceReceipt ?? null,
              }
            : {
                isRefundMode: false,
                refundSourceReceipt: null,
              }),
        })
      },

      // Cart management
      addToCart: (product, quantity = 1, lotNumberOrId = null, options) => {
        const { cart, currentReceiptId, selectedCustomer, serverTotals, isRefundMode, loadedEcomMeta } = get()
        // Hard gate on partially-paid receipts. Without this, an add()
        // clobbers serverTotals (line 530 below) and the next
        // CheckoutModal open hasPartialPaid=false, the modal pre-fills
        // amountDue to the new full cart total, and the cashier would
        // re-charge the already-collected portion. Route handler
        // (handleAddToCart) shows the toast; this is the data-safety
        // backstop for any other caller (ProductModal, scan handler,
        // command palette).
        if (isReceiptPartiallyPaid(serverTotals, isRefundMode)) return
        // Refund carts are seeded from the source receipt and only ever
        // shrink -- there is no "return something that wasn't bought".
        // An add here flips a −1 refund line to 0 or +1, which turns the
        // return into a sale: it can never settle to zero, so the cashier
        // abandons the attempt and the tender they already recorded is
        // stranded as a Canceled JT that permanently zeroes the source
        // receipt's refundable cap. Every rail then reads "up to $0.00"
        // and that sale can never be refunded again (four stores locked
        // out this way by 2026-08-24). The route handler shows the toast;
        // this is the data-safety backstop for callers that bypass it
        // (ProductModal, command palette, the scan path). BE-enforced too
        // -- PosReceiptItem.update_from_request rejects a positive qty on
        // a return.
        if (isRefundMode) return
        // Online-PAID pickup view: the cart must match the charged order
        // exactly -- no adds from any caller. (No partial changes by
        // design; cancel the whole order and re-ring in store instead.)
        if (loadedEcomMeta?.paidOnline) return
        // Merchandising backstop, mirroring the till (waio-portal
        // ops_receipt._inactive_product_error): a store with
        // `frontend.hide_out_of_stock` on refuses inactive / flagged
        // out-of-stock products, so the register must not put one in the
        // cart -- /cart/preview drops the line (the cart says why, but
        // only after the fact) and /cart/commit 400s the whole cart. The
        // route's
        // handleAddToCart has the same gate; this backstop covers the
        // callers that bypass it (ProductModal, LotSelectModal,
        // LeftSidebar, the recent-products panel, "buy again").
        //
        // Only a STORE-scoped row can answer this: `is_inactive` /
        // `out_of_stock` exist with the same names on the head-office
        // master row (/objects/products/{id}, which CustomerModal's
        // insights hand to ProductModal), where they describe HO, not
        // this store. Master rows carry no `on_hand`; store rows
        // (/products/search, by-barcode) always do, so its presence is
        // the marker. Never silent: the cashier gets the same toast the
        // route path shows, otherwise the Add button just does nothing.
        //
        // Read via the snapshot getter rather than a hook: this is a
        // plain zustand action. `null` (config not loaded yet) leaves the
        // gate OPEN -- same as a store that never set the SysConfig.
        if (
          getPOSConfigSnapshot()?.posHideOutOfStock
          && product.on_hand != null
          && (product.is_inactive === true || product.out_of_stock === true)
        ) {
          useUIStore.getState().showToast({
            type: 'warning',
            title: product.is_inactive ? 'Inactive at this store' : 'Marked out of stock',
            message: `${product.display_name ?? product.description ?? 'This product'} cannot be sold here until the flag is cleared in the product settings.`,
          })
          return
        }
        const shouldDetachStaleReceipt = Boolean(currentReceiptId && cart.length === 0)
        // A first line landing in an empty cart begins a new transaction, so
        // a previously-answered ID check must not carry over. clearCart /
        // clearReceipt cover the tidy paths (pay, void, hold); this covers
        // the rest -- most importantly the cashier who answers the prompt,
        // backs out of the tender modal, strips the cart line by line, and
        // rings a different customer through.
        const startsNewTransaction = cart.length === 0

        // lotNumberOrId can be either a lot_id (number) or lot_number (string/batch_number)
        const lotNumber = typeof lotNumberOrId === 'string' ? lotNumberOrId : null
        const lotId = typeof lotNumberOrId === 'number' ? lotNumberOrId : null

        // Grouping toggle: when `forceNewLine` is set, never dedupe -- each
        // call gets its own row. Mirrors the BE `pos.receipt.group_items`
        // SysConfig: false there means each scan is a separate line, so
        // the FE must produce a cart shape /cart/preview can reproduce.
        const existingIndex = options?.forceNewLine
          ? -1
          : cart.findIndex(
              item =>
                item.product_id === product.id
                && item.lot_number === lotNumber,
            )

        if (existingIndex >= 0) {
          // Update quantity of existing item
          const updatedCart = [...cart]
          const existingItem = updatedCart[existingIndex]
          const newQuantity = existingItem.quantity + quantity
          updatedCart[existingIndex] = {
            ...existingItem,
            quantity: newQuantity,
            discount_amount: scaleLineDiscount(
              existingItem.discount_amount ?? 0,
              existingItem.quantity,
              newQuantity,
            ),
            tax_amount: 0,
            line_total: existingItem.unit_price * newQuantity,
          }

          // A quantity bump IS a re-price, so serverTotals must be dropped
          // unconditionally -- not only when the receipt is being detached.
          // It used to survive here, and because cartTotal/amountDue both
          // prefer serverTotals.amountToPay over the live /cart/preview total
          // (CheckoutModal L314/L419), the "Amount to Pay" field stayed frozen
          // at the recall-time balance. Scanning a lot-confirm barcode for a
          // product already on a recalled online order lands in THIS branch,
          // so the order silently grew by a unit while the terminal was still
          // charged the pre-scan total -- leaving a phantom remaining balance
          // (2026-08-19, receipt 826081923054: committed $62.94, charged
          // $57.35, $5.59 stranded).
          set({
            cart: updatedCart,
            serverTotals: null,
            ...(shouldDetachStaleReceipt ? { currentReceiptId: null } : {}),
            ...(startsNewTransaction ? { ageChecked: false } : {}),
          })
        } else {
          // Add new item to cart with the effective per-unit price for this
          // customer (sale > tier > regular). Server `/cart/preview` is
          // authoritative — CartItemRow swaps in priced.price/subtotal once
          // it returns — but this value flows into item.unit_price /
          // line_total and shows for the ~250ms before the first preview,
          // and any time the priced lookup misses. Pass selectedCustomer
          // so the optimistic estimate gates the member-tier `price2`
          // correctly (walk-ins should see regular; see lib/pricing.ts).
          const unitPrice = effectiveUnitPrice(product, selectedCustomer)

          // line_total is estimated client-side as price × qty.
          // For cannabis, price is tax-inclusive so this is the correct display total.
          // For non-cannabis, price is pre-tax but we show it without tax here — the checkout
          // will use server-confirmed totals (receipt.amount_to_pay) once the receipt is created.
          // Deposit is NOT added here — backend adds it when item is saved to receipt.
          const newItem: CartItem = {
            id: generateCartItemId(),
            product_id: product.id,
            product,
            quantity,
            unit_price: unitPrice,
            discount_amount: 0,
            discount_percent: 0,
            tax_amount: 0,
            line_total: unitPrice * quantity,
            lot_id: lotId,
            lot_number: lotNumber,
            notes: null,
          }

          // Clear server totals — they're stale once cart changes
          set({
            cart: [...cart, newItem],
            serverTotals: null,
            ...(shouldDetachStaleReceipt ? { currentReceiptId: null } : {}),
            ...(startsNewTransaction ? { ageChecked: false } : {}),
          })
        }
      },

      updateCartItem: (itemId, updates) => {
        const { cart, isRefundMode, serverTotals, loadedEcomMeta } = get()
        // Online-PAID pickup view: read-only mirror of the charged order.
        if (loadedEcomMeta?.paidOnline) return
        // The delivery fee line is read-only (no qty/price/discount edits).
        if (isDeliveryLine(cart, itemId)) return
        // Same partial-payment gate as addToCart. Blocks discount
        // changes, qty mutations, batch/lot swaps, and any other field
        // mutation that would shift the cart shape against a
        // BE-charged ledger.
        if (isReceiptPartiallyPaid(serverTotals, isRefundMode)) return
        const updatedCart = cart.map(item => {
          if (item.id !== itemId) return item

          const updatedItem = { ...item, ...updates }
          return {
            ...updatedItem,
            line_total: calculateLineTotal(updatedItem),
          }
        })

        // Refund mode skips /cart/preview, so serverTotals is the only
        // source of truth for tax/total. The ProductModal refund branch
        // re-seeds it from the receipt PUT response after this call;
        // nuking it here would briefly flash "$0.00" tax in between.
        // Sale mode still nukes so the next /cart/preview can authoritatively
        // replace it.
        set(isRefundMode
          ? { cart: updatedCart }
          : { cart: updatedCart, serverTotals: null })
      },

      changeBatch: (itemId, newBatchNumber) => {
        const { cart, isRefundMode, serverTotals, loadedEcomMeta } = get()
        // Online-PAID pickup view: read-only mirror of the charged order.
        if (loadedEcomMeta?.paidOnline) return
        // The delivery fee line has no lot to swap.
        if (isDeliveryLine(cart, itemId)) return
        // Lot swap on a partially-paid line is a no-op rather than a
        // sync hazard. Allowing it would clear serverTotals and lose
        // the partial-payment context on the next modal open.
        if (isReceiptPartiallyPaid(serverTotals, isRefundMode)) return
        const updatedCart = cart.map(item => {
          if (item.id === itemId) {
            return { ...item, lot_number: newBatchNumber }
          }
          return item
        })
        set({ cart: updatedCart, serverTotals: null })
      },

      confirmScanLine: (itemId, lotNumber) => {
        const { cart } = get()
        const updatedCart = cart.map((item) => {
          if (item.id !== itemId) return item
          return {
            ...item,
            scan_confirmed: true,
            // A GS1 scan (AI 10) overrides the FIFO default with the physical
            // batch; a plain-UPC scan confirms presence and keeps the lot.
            ...(lotNumber ? { lot_number: lotNumber } : {}),
          }
        })
        // Preserve serverTotals — confirming a lot is metadata, not a
        // re-price, so the order's confirmed totals shouldn't flash.
        set({ cart: updatedCart })
      },

      removeFromCart: (itemId) => {
        const { cart, isRefundMode, serverTotals, loadedEcomMeta } = get()
        // Online-PAID pickup view: read-only mirror of the charged order.
        if (loadedEcomMeta?.paidOnline) return
        // The delivery fee can't be removed — it's part of the order.
        if (isDeliveryLine(cart, itemId)) return
        // Same partial-payment gate as the other mutators. The cart
        // row's X button is already disabled via the paymentLocked
        // selector in CartItemRow; this is the defense-in-depth for
        // any caller that bypasses the row (keyboard shortcuts go
        // through RightSidebar.handleRemove which has its own gate,
        // but command-palette / scan-driven removals route here too).
        if (isReceiptPartiallyPaid(serverTotals, isRefundMode)) return
        const nextCart = cart.filter(item => item.id !== itemId)
        // Refund mode: keep serverTotals + currentReceiptId intact.
        // useRefundCartMutations re-seeds serverTotals from the BE
        // DELETE response BEFORE calling this; nulling them here would
        // clobber the just-set values and drop CartSummary to local
        // estimation (which has no tax in refund mode -> displays
        // "Tax $0.00" + "Refund Total = subtotal"). currentReceiptId
        // must also persist so the cashier can void the BE refund
        // receipt even after removing the last line; otherwise the
        // void path has no id to PUT against. Sale mode keeps the
        // legacy behavior: null serverTotals so the next /cart/preview
        // can replace them authoritatively, and detach the receipt id
        // on empty so a fresh sale doesn't reuse a stale id.
        set(isRefundMode
          ? { cart: nextCart }
          : {
              cart: nextCart,
              serverTotals: null,
              ...(nextCart.length === 0 ? { currentReceiptId: null } : {}),
            })
      },

      updateQuantity: (itemId, quantity) => {
        const { cart, isRefundMode, serverTotals, loadedEcomMeta } = get()
        // Online-PAID pickup view: read-only mirror of the charged order.
        if (loadedEcomMeta?.paidOnline) return
        // Delivery fee qty is fixed at 1 — never decrement/remove it.
        if (isDeliveryLine(cart, itemId)) return
        // Same partial-payment gate as the other mutators. Defense for
        // keyboard / scan / command-palette qty paths.
        if (isReceiptPartiallyPaid(serverTotals, isRefundMode)) return
        const existing = cart.find((item) => item.id === itemId)
        if (!existing) return

        const isRefundItem = existing.quantity < 0
        if (!isRefundItem && quantity <= 0) {
          get().removeFromCart(itemId)
          return
        }
        if (isRefundItem && quantity >= 0) {
          // Keep at least one refund quantity. Removal must be explicit.
          return
        }

        const updatedCart = cart.map(item => {
          if (item.id !== itemId) return item

          return {
            ...item,
            quantity,
            discount_amount: scaleLineDiscount(
              item.discount_amount ?? 0,
              item.quantity,
              quantity,
            ),
            tax_amount: 0,
            line_total: item.unit_price * quantity,
          }
        })

        set({ cart: updatedCart })
      },

      changeQuantity: (itemId, delta) => {
        const { cart, updateQuantity, removeFromCart } = get()
        const item = cart.find(i => i.id === itemId)
        if (!item) return
        // Delivery fee is fixed — no +/- (and -1 at qty 1 would remove it).
        if (item.product_id === DELIVERY_PRODUCT_ID) return

        const newQuantity = item.quantity + delta
        const isRefundItem = item.quantity < 0
        if (isRefundItem) {
          if (newQuantity >= 0) return
          updateQuantity(itemId, newQuantity)
          return
        }

        if (newQuantity <= 0) {
          removeFromCart(itemId)
        } else {
          updateQuantity(itemId, newQuantity)
        }
      },

      // Refresh cart-line prices from authoritative receipt items returned
      // by the server.
      //
      // Reconciliation key:
      //   - Basket children (item.basket_id set): collapsed under
      //     `${basket_id}|` — all siblings sum into the basket cart line.
      //     unit_price = basket-line price (line_total / basket_qty),
      //     line_total = sum of children's totals,
      //     discount_amount = sum of children's discounts.
      //     Stamps cart line `basket_id` so the UI can disable qty/price
      //     edits (basket scaling not supported server-side).
      //   - Regular items: keyed by `${product_id}|${lot_number}` with
      //     unit_price/total/discount taken directly from the receipt item.
      //
      // Quantity, lot, and id are owned by the cart and never overwritten.
      applyServerItemPrices: (items) => {
        if (!items || items.length === 0) {
          if (import.meta.env.DEV) {
            console.warn('[applyServerItemPrices] called with no items — cart prices will not refresh')
          }
          return
        }

        type Aggregate = {
          unit_price: number
          discount_amount: number
          line_total: number
          is_basket: boolean
          basket_id?: number | null
        }
        const byKey = new Map<string, Aggregate>()

        for (const it of items) {
          const qty = Math.abs(typeof it.qty === 'number' ? it.qty : parseFloat(String(it.qty)) || 0)
          const price = typeof it.price === 'number' ? it.price : parseFloat(String(it.price)) || 0
          // line_total is the PRE-TAX line subtotal (what this line
          // contributes to receipt subtotal). Tax is shown only in the
          // dedicated tax row, never embedded in the cart-line amount —
          // that way `sum(cart line totals) === serverTotals.subtotal`
          // and the cashier sees the same number on the cart line as on
          // the product card. Prefer the server's `subtotal` field;
          // fall back to qty × price if the field is missing.
          const subtotal = it.subtotal != null
            ? (typeof it.subtotal === 'number' ? it.subtotal : parseFloat(String(it.subtotal)) || 0)
            : price * qty
          const discount = it.discount_amount != null
            ? (typeof it.discount_amount === 'number' ? it.discount_amount : parseFloat(String(it.discount_amount)) || 0)
            : 0

          if (it.basket_id) {
            // Basket child: aggregate under the basket SKU's key. The cart
            // line's unit_price is the per-basket price (sum of children
            // ÷ basket_qty); line_total is the sum of children's pre-tax
            // subtotals (matches the receipt subtotal contribution).
            const key = `${Number(it.basket_id)}|`
            const basketQty = Math.abs(
              typeof it.basket_qty === 'number'
                ? it.basket_qty
                : parseFloat(String(it.basket_qty ?? '')) || 1,
            ) || 1
            const existing = byKey.get(key)
            if (existing) {
              existing.line_total += subtotal
              existing.discount_amount += discount
              // unit_price recomputed below from the running total
            } else {
              byKey.set(key, {
                unit_price: 0, // filled in below
                discount_amount: discount,
                line_total: subtotal,
                is_basket: true,
                basket_id: Number(it.basket_id),
              })
            }
            const agg = byKey.get(key)!
            agg.unit_price = basketQty > 0 ? agg.line_total / basketQty : agg.line_total
          } else {
            const key = `${it.product_id}|${it.lot_number ?? ''}`
            if (!byKey.has(key)) {
              byKey.set(key, {
                unit_price: price,
                discount_amount: discount,
                line_total: subtotal,
                is_basket: false,
                basket_id: null,
              })
            }
          }
        }

        const cart = get().cart
        let changed = false
        const unmatchedCartKeys: string[] = []
        const updatedCart = cart.map((c) => {
          const key = `${c.product_id}|${c.lot_number ?? ''}`
          let server = byKey.get(key)
          // Fallback: cart line carries a `lot_number` (often seeded from
          // `product.lot_number` in ProductModal) but the server item came
          // back without one — happens when the backend product doesn't
          // have batch tracking enabled, so it stores the cart line as a
          // plain product line. Match by `${product_id}|` instead. We
          // only do this when there's exactly one cart line for this
          // product so we don't accidentally collapse multiple distinct
          // lot lines into the same server entry.
          if (!server && c.lot_number) {
            const cartLinesForProduct = cart.filter((other) => other.product_id === c.product_id).length
            if (cartLinesForProduct === 1) {
              server = byKey.get(`${c.product_id}|`)
            }
          }
          if (!server) {
            if (import.meta.env.DEV) unmatchedCartKeys.push(key)
            return c
          }

          const sign = c.quantity < 0 ? -1 : 1
          const nextLineTotal = sign * Math.abs(server.line_total)
          const nextBasketId = server.is_basket ? server.basket_id ?? c.product_id : null
          if (
            c.unit_price === server.unit_price &&
            (c.discount_amount ?? 0) === server.discount_amount &&
            c.line_total === nextLineTotal &&
            (c.basket_id ?? null) === nextBasketId
          ) {
            return c
          }
          changed = true
          return {
            ...c,
            unit_price: server.unit_price,
            discount_amount: server.discount_amount,
            line_total: nextLineTotal,
            basket_id: nextBasketId,
          }
        })

        // Surface key-mismatches in dev so we can diagnose
        // applyServerItemPrices no-ops without instrumenting at every
        // call site. Most common cause is a `lot_number` set on the
        // cart item but absent from the server item (or vice versa) —
        // the keys differ and the cart line silently keeps its stale
        // shelf price even though the receipt has the correct tier
        // price.
        if (import.meta.env.DEV && unmatchedCartKeys.length > 0) {
          console.warn(
            '[applyServerItemPrices] cart lines did not match any server item — prices stale:',
            {
              unmatchedCartKeys,
              serverKeys: Array.from(byKey.keys()),
              serverItems: items.map((it) => ({
                id: it.id,
                product_id: it.product_id,
                lot_number: it.lot_number,
                price: it.price,
                qty: it.qty,
                basket_id: it.basket_id,
              })),
            },
          )
        }

        if (changed) {
          set({ cart: updatedCart })
        }
      },

      addGiftCardLine: (item, balance) => {
        const { cart } = get()
        const last4 = item.gift_card_number ? item.gift_card_number.slice(-4) : ''
        // BE description is just "Gift Card" without the card-number suffix;
        // append the last-4 ourselves so the cart line + receipt printout
        // both identify which card this is.
        const baseName = item.description || 'Gift Card'
        const hasSuffix = /#\d+$/.test(baseName)
        const description = !hasSuffix && last4 ? `${baseName} #${last4}` : baseName
        const syntheticProduct: Product = {
          id: item.product_id,
          description,
          price: balance,
          is_cannabis: false,
          deposit_price: 0,
        }
        const newItem: CartItem = {
          id: generateCartItemId(),
          product_id: item.product_id,
          product: syntheticProduct,
          quantity: 1,
          unit_price: balance,
          discount_amount: 0,
          discount_percent: 0,
          tax_amount: 0,
          line_total: balance,
          lot_id: null,
          lot_number: null,
          notes: null,
          receipt_item_id: item.id,
          // Flag so the preview bridge sends `price` as a manual_price
          // override and the customer-change reprice leaves this row alone
          // — gift cards have no catalog price; balance is the price.
          manual_price: true,
          gift_card_number: item.gift_card_number,
          gift_card_balance: balance,
        }
        set({ cart: [...cart, newItem], serverTotals: null })
      },

      clearCart: () => {
        // Wipe everything tied to the current cart, including the
        // backend receipt linkage. Earlier this only cleared cart +
        // customer locally, which left `currentReceiptId` pointing at a
        // server-side receipt that still had its original customer
        // attached. Adding a product after that re-used the stale
        // receipt; selecting a new customer then 409'd because the
        // backend's prev_customer_id never matched the user's mental
        // model. Now matches clearReceipt's wipe semantics.
        const { currentReceiptId } = get()
        set({
          cart: [],
          selectedCustomer: null,
          currentReceiptId: null,
          loadedEcomMeta: null,
          lastCompletedReceiptId: currentReceiptId ?? get().lastCompletedReceiptId,
          serverTotals: null,
          isRefundMode: false,
          refundSourceReceipt: null,
          refundSourcePayments: null,
          // New transaction => the ID check must be answered again.
          ageChecked: false,
          pendingReward: null,
          pendingOffer: null,
        })
        // Pair-up cartStore reset, same reason as clearReceipt — the v2
        // cart shape (lines, partialPayments, preview) is
        // owned by a separate store and must die with the receipt.
        useCartStore.getState().clear()
      },

      clearReceipt: () => {
        const { currentReceiptId } = get()
        set({
          currentReceiptId: null,
          loadedEcomMeta: null,
          lastCompletedReceiptId: currentReceiptId ?? get().lastCompletedReceiptId,
          cart: [],
          serverTotals: null,
          selectedCustomer: null,
          searchQuery: '',
          // Refund state is tied to a specific receipt — once that receipt is
          // detached (held, voided, completed) refund mode must reset too,
          // otherwise the "Refunding Receipt" banner sticks around for the
          // next sale and refund-styled totals are applied incorrectly.
          isRefundMode: false,
          refundSourceReceipt: null,
          refundSourcePayments: null,
          // New transaction => the ID check must be answered again.
          ageChecked: false,
          pendingReward: null,
          pendingOffer: null,
        })
        // Reset the v2 cartStore too. It holds independent transaction
        // state (lines, partialPayments, preview) that must die with the
        // receipt. The receipt binding now lives in currentReceiptId
        // (nulled above), so a recall→void flow can't leave the next
        // /cart/commit building with `receipt_id: <voided-id>` and tripping
        // "Receipt is locked (status=-1)" on the BE. This mirrors the
        // pair-up pattern already used in useHoldCurrentReceipt and
        // CheckoutModal's already_paid / duplicate_transaction branches.
        useCartStore.getState().clear()
      },

      // Customer management
      setCustomer: (customer) => {
        // Same partial-payment lock as the cart mutators. A customer
        // swap re-prices line items at the customer's tier, which would
        // shift the cart total against a BE that already has payment
        // JTs riding on the previous shape. useSelectCustomer (the UI
        // path) gates this with a user-visible toast; this is the
        // data-safety backstop for callers that bypass the hook
        // (AgeVerifyModal auto-attaches a verified customer on ID scan,
        // for instance).
        if (isReceiptPartiallyPaid(get().serverTotals, get().isRefundMode)) return
        // Optimistically reprice existing cart lines to the new customer's
        // effective unit price (regular / member-tier `price2` / scheduled
        // sale, whichever wins). Without this, an item added before the
        // cashier picked a customer keeps its catalog `unit_price` /
        // `line_total` and the cart row visibly disagrees with the
        // server-confirmed Subtotal/Total below — `/cart/preview` returns
        // the right priced rows for the new customer, but those values
        // don't flow back to posStore in the v2 preview-only path
        // (`applyServerItemPrices` only runs when a backing receipt
        // exists, which is no longer the case during cart-build).
        // /cart/preview is still authoritative; this just keeps the
        // optimistic legacy fields aligned so cart UI components that
        // read `item.unit_price` / `item.line_total` show the customer-
        // tier price immediately. Skip basket lines — their prices are
        // aggregated from children server-side.
        const { cart, selectedCustomer } = get()
        // Pending SpringBig redemptions belong to the previous customer's
        // member record — switching customers must drop them so we don't
        // post the wrong member's reward_id at pay-time.
        const customerChanged = (selectedCustomer?.id ?? null) !== (customer?.id ?? null)
        let changed = false
        const repriced = cart.map((item) => {
          if ((item.basket_id ?? null) !== null) return item
          // Don't clobber a cashier's manual price override — that's a
          // deliberate choice that should survive a customer change.
          if (item.manual_price) return item
          const newUnitPrice = effectiveUnitPrice(item.product, customer)
          if (item.unit_price === newUnitPrice) return item
          changed = true
          return {
            ...item,
            unit_price: newUnitPrice,
            line_total: newUnitPrice * item.quantity,
          }
        })
        // A SpringBig PERCENT redemption rides cartStore.discount carrying the
        // previous member's offer_id/reward_id (set by LoyaltyRedemptionPicker).
        // Like pendingReward/pendingOffer, it belongs to that member, so drop
        // it on ANY customer change -- otherwise a same-group A->B switch keeps
        // A's ids in the cart discount and checkout posts the redemption under
        // B. A plain manual discount carries no id and is left to
        // useSelectCustomer's group-change clear (it survives a same-group
        // switch by design). setDiscount no-ops on partially-paid receipts, but
        // setCustomer already bailed above in that case, so this runs clean.
        if (customerChanged) {
          const cs = useCartStore.getState()
          const d = cs.discount
          if (d && (d.offer_id || d.reward_id)) {
            cs.setDiscount(null)
          }
        }
        set({
          selectedCustomer: customer,
          ...(changed ? { cart: repriced } : {}),
          // A different customer is a different person to ID, so the
          // answer given for the previous one must not stand. Rides the
          // existing `customerChanged` test so re-selecting the same
          // customer doesn't re-arm the prompt.
          ...(customerChanged
            ? { pendingReward: null, pendingOffer: null, ageChecked: false }
            : {}),
        })
      },

      // UI actions
      setViewMode: (mode) => set({ viewMode: mode }),
      // Invariant: no text input keeps focus across a tab change — a
      // parked focus turns the next barcode scan into typed text
      // (docs/scan-focus-plan.md). Runs BEFORE the switch so a tab
      // that autofocuses its own input on mount (CustomersTab) wins.
      setActiveTab: (tab) => {
        blurActiveTextInput()
        set({ activeTab: tab })
      },
      setSearchQuery: (query) => set({ searchQuery: query }),
      setSelectedCategory: (categoryId) => set({ selectedCategoryId: categoryId }),
      setUIFilters: (filters) => set({ uiFilters: filters }),
      clearUIFilters: () => set({ uiFilters: DEFAULT_UI_FILTERS }),

      // Refund actions
      enterRefundMode: (sourceReceipt, sourcePayments = null) => {
        set({
          isRefundMode: true,
          refundSourceReceipt: sourceReceipt,
          refundSourcePayments: sourcePayments ?? null,
          cart: [],
          currentReceiptId: sourceReceipt?.id ?? null,
          serverTotals: null,
          selectedCustomer: sourceReceipt.customer || null,
        })
      },

      exitRefundMode: () => {
        set({
          isRefundMode: false,
          refundSourceReceipt: null,
          refundSourcePayments: null,
          isRefundMutating: false,
          cart: [],
          currentReceiptId: null,
          serverTotals: null,
          selectedCustomer: null,
        })
      },

      updateRefundReceipt: (receipt) => {
        // No-op outside refund mode -- defensive against the success
        // path firing this after exitRefundMode() ran on the same tick.
        if (!get().isRefundMode) return
        set({ refundSourceReceipt: receipt })
      },

      loadRefundItems: (items, productMap) => {
        const cartItems: CartItem[] = items.map((item: any) => {
          const qty = Math.abs(typeof item.qty === 'number' ? item.qty : parseFloat(String(item.qty)) || 1)
          const unitPrice = typeof item.price === 'number' ? item.price : parseFloat(String(item.price)) || 0
          const subtotal = item.subtotal != null
            ? (typeof item.subtotal === 'number' ? item.subtotal : parseFloat(String(item.subtotal)) || 0)
            : null
          const lineSubtotal = subtotal != null
            ? Math.abs(subtotal)
            : unitPrice * qty

          const sourceDiscountAmount = Math.abs(
            typeof item.discount_amount === 'number'
              ? item.discount_amount
              : parseFloat(String(item.discount_amount ?? '')) || 0,
          )
          const sourceDiscountPercent =
            typeof item.discount === 'number'
              ? item.discount
              : parseFloat(String(item.discount ?? '')) || 0
          const sourceReason =
            typeof item.discount_reason_other === 'string'
              ? item.discount_reason_other
              : null

          // Receipt items expose the product image as `main_image`;
          // map it through so the cart row renders the actual artwork
          // instead of the placeholder tile.
          const itemImage =
            (typeof item.main_image === 'string' && item.main_image)
            || (typeof item.image_url === 'string' && item.image_url)
            || (typeof item.image === 'string' && item.image)
            || null

          // Spread the full Product (when the refund flow resolved one
          // via /objects/products/{id}) underneath so display-only
          // fields (image, brand, strain, THC/CBD %, on_hand, tags)
          // come through. Mirrors loadReceiptIntoCart's pattern. The
          // receipt-derived values below win: price/qty are receipt
          // facts; is_cannabis from the receipt determines tax behavior.
          const fullProduct = productMap?.get(item.product_id) ?? null
          const syntheticProduct: Product = {
            ...(fullProduct ?? {}),
            id: item.product_id,
            description:
              item.description || fullProduct?.description || `Product ${item.product_id}`,
            price: unitPrice,
            is_cannabis: item.is_cannabis ?? fullProduct?.is_cannabis ?? false,
            ...(itemImage ? { image_url: itemImage } : {}),
          }

          // Backend receipt-item id — kept so the refund sync hook can
          // PUT/DELETE qty changes against the right row.
          const receiptItemId = typeof item.id === 'number'
            ? item.id
            : (typeof item.id === 'string' ? Number(item.id) : NaN)

          // /copy-with-refund items have used both `lot_number` (legacy)
          // and `batch_number` (newer batches contract) over time; read
          // either so the refund cart line carries the source batch
          // forward and the modal seeds onto it. Without this the cart
          // row's LOT badge is missing and the cashier loses the FIFO
          // assignment from the original sale.
          const sourceLot =
            (typeof item.lot_number === 'string' && item.lot_number)
            || (typeof item.batch_number === 'string' && item.batch_number)
            || null

          return {
            id: `refund_${item.product_id}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
            product_id: item.product_id,
            product: syntheticProduct,
            quantity: -qty,
            unit_price: unitPrice,
            discount_amount: sourceDiscountAmount,
            discount_percent: sourceDiscountPercent,
            tax_amount: 0,
            line_total: -lineSubtotal,
            lot_id: null,
            lot_number: sourceLot,
            notes: sourceReason,
            max_refund_qty: qty,
            ...(Number.isFinite(receiptItemId) ? { receipt_item_id: receiptItemId } : {}),
          }
        })

        set({ cart: cartItems })
      },

      // Computed helpers
      getCartTotal: () => {
        const { cart } = get()
        return cart.reduce((total, item) => total + item.line_total, 0)
      },

      getCartItemCount: () => {
        const { cart } = get()
        return cart.reduce((count, item) => count + Math.abs(item.quantity), 0)
      },
    }),
    {
      name: 'waio-pos-cart',
      partialize: (state) => {
        // Persist the recovery triplet (currentReceiptId + serverTotals
        // + selectedCustomer) as a UNIT whenever we're NOT in refund
        // mode. Keeping the three together is what makes rehydration
        // safe: the hazard the original author guarded against was a
        // "customer without a receipt id" half-state, where the next
        // cashier action silently attaches the cart to the wrong/stale
        // BE receipt. We never create that half-state here -- either all
        // three persist or none do.
        //
        // Why broadening past the old partial-payment-only gate is safe:
        // in the v2 architecture, building a cart is purely local
        // (/cart/preview is stateless; the per-keystroke
        // useSyncReceiptTotals hook is intentionally NOT mounted -- see
        // routes/pos/index.tsx). So a fresh in-progress cart has
        // currentReceiptId == null, and this branch persists just the
        // selectedCustomer -- the thing the cashier was losing on every
        // refresh -- with no receipt id to go stale and a clean receipt
        // created at Pay. A non-null currentReceiptId only exists after
        // a recall/unhold or a (partial) payment, which is exactly the
        // mid-transaction context the cashier MUST keep across a
        // refresh: the banner, the Continue-Payment button, and the
        // partial-payment lock guards all gate on serverTotals being
        // set. Real production case: "log back into the POS, see this...
        // stuck in calculating" -- none of that context survived before.
        //
        // Stale-receipt risk (BE marked the receipt paid in another tab,
        // BE timed out the receipt, etc.) is handled downstream at Pay
        // time: ops_payment.create_payment returns `already_paid` if the
        // receipt is terminal, and the checkout modal re-fetches
        // /receipts/{id} on open, which surfaces any drift.
        //
        // Refund state stays unpersisted: rehydrating just isRefundMode
        // without the BE refund receipt id leaves the cashier in the
        // half-state the merge() below cleans up ("Refund Line Not
        // Linked").
        return {
          cart: state.cart,
          ...(state.isRefundMode ? {} : {
            currentReceiptId: state.currentReceiptId,
            // Persisted alongside currentReceiptId so a reload mid-view still
            // knows the loaded order is an ecom order to Close (not Void).
            loadedEcomMeta: state.loadedEcomMeta,
            serverTotals: state.serverTotals,
            selectedCustomer: state.selectedCustomer,
          }),
        }
      },
      // Strip orphan refund cart items on rehydration. Background:
      // `cart` is persisted but the surrounding refund state is not
      // (see partialize comment). After a reload only the cart rows
      // come back; currentReceiptId, isRefundMode, and the source-
      // receipt/payments slots all reset to defaults. Refund-mode
      // cart items have negative quantity (see updateQuantity
      // line 594 / changeQuantity line 629 / loadRefundItems
      // line 1064 -- negative quantity is the canonical refund-item
      // signal in this store), and they carry `receipt_item_id`
      // pointing at the BE refund destination. Without this
      // cleanup the cashier sees what looks like a refund cart, but
      // the route's gating expression
      //     isRefundMode ? refundReceiptId : null
      // routes interactions through useRefundCartMutations only when
      // `isRefundMode` is true. When the persisted negative-qty rows
      // are present and the cashier triggers refund mode (e.g. by
      // recalling and refunding ANOTHER receipt), the orphan rows
      // become live targets of refund handlers that find a stale
      // receipt_item_id with no live refundReceiptId -- producing
      // the "Refund Line Not Linked" toast or worse, mutations
      // against the wrong receipt.
      //
      // Dropping these on the way in trades cashier work-loss
      // (uncommon: a refund-in-progress survives only a hard reload,
      // which itself is rare during a transaction) for the
      // guarantee that the cart can never silently disagree with
      // the BE refund receipt across sessions. The BE refund
      // destination, if one was created, lingers as a NEW-status
      // orphan that an Orders-search surface or admin cleanup
      // should pick up -- preferable to letting the cashier
      // unknowingly process a refund payment against an
      // out-of-sync local cart.
      //
      // Implemented via `merge` rather than `onRehydrateStorage`
      // direct mutation. merge is zustand's canonical hook for
      // transforming the rehydrated state into the final initial
      // state; the returned object becomes the store's state without
      // any intermediate render exposing orphans. Direct state
      // mutation in onRehydrateStorage produces the same in-memory
      // result but bypasses immutability conventions and reads
      // awkwardly (`state.cart = ...` as a side effect rather than
      // the canonical "transform then return"). Both notify
      // subscribers post-rehydration with the cleaned state; merge
      // is the more declarative shape.
      merge: (persistedState, currentState) => {
        if (!persistedState || typeof persistedState !== 'object') {
          return currentState
        }
        const persisted = persistedState as { cart?: unknown }
        if (!Array.isArray(persisted.cart)) {
          return { ...currentState, ...persisted } as POSStore
        }
        const cart = persisted.cart as CartItem[]
        const orphans = cart.filter((item) => item.quantity < 0)
        if (orphans.length > 0) {
          console.warn(
            '[posStore] Dropping %d orphan refund cart item(s) on rehydration '
              + '-- refund state (currentReceiptId, isRefundMode) is not '
              + 'persisted; the BE refund receipt (if any) is now an '
              + 'untracked NEW-status orphan.',
            orphans.length,
          )
          return {
            ...currentState,
            ...persisted,
            cart: cart.filter((item) => item.quantity >= 0),
          } as POSStore
        }
        return { ...currentState, ...persisted } as POSStore
      },
    }
  )
)
